import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { AccordionSection } from "./AccordionSection";
import { SectionErrors } from "./SectionErrors";
import { RadioQuestion } from "./RadioQuestion";
import { SignedImage } from "../../common/SignedImage";

export function GasSignOffSection({ jobToComplete, gasSignOffHandler, uploadCP1ToClientFunc, errorMessage }) {
  const isComplete = jobToComplete.completionDetail.gasSignOffComplete;
  const cd = jobToComplete.completionDetail;
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [selectedFileName, setSelectedFileName] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFileName(e.target.files[0].name);
    }
    uploadCP1ToClientFunc(e);
  };

  const yesNoOptions = (trueChecked, falseChecked, yesId, noId) => [
    { id: yesId, value: true, label: "Yes", defaultChecked: trueChecked },
    { id: noId, value: false, label: "No", defaultChecked: falseChecked },
  ];

  return (
    <AccordionSection
      id="gasSignOff"
      headerId="headerGasSignOff"
      collapseId="collapseGasSignOff"
      number={4}
      title="Gas Installation Sign Off"
      isComplete={isComplete}
    >
      <form onSubmit={gasSignOffHandler}>
        <RadioQuestion
          label="Visual inspection satisfactory?"
          name="gasVisualInspection"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasVisualInspectionOk,
            isComplete ? !cd.gasVisualInspectionOk : "",
            "gasVisualInspectionYes",
            "gasVisualInspectionNo",
          )}
        />
        <RadioQuestion
          label="Gas tightness test satisfactory?"
          name="gasTightnessTest"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasTightnessTestOk,
            isComplete ? !cd.gasTightnessTestOk : "",
            "gasTightnessTestYes",
            "gasTightnessTestNo",
          )}
        />
        <RadioQuestion
          label="Are the emergency controls accessible?"
          name="gasEmergencyControlsAccess"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasEmergencyControlAccessible,
            isComplete ? !cd.gasEmergencyControlAccessible : "",
            "gasEmergencyControlsAccessYes",
            "gasEmergencyControlsAccessNo",
          )}
        />
        <RadioQuestion
          label="Equipotential satisfactory?"
          name="gasEquipotential"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasEquipotentialBondingOk,
            isComplete ? !cd.gasEquipotentialBondingOk : "",
            "gasEquipotentialYes",
            "gasEquipotentialNo",
          )}
        />
        <RadioQuestion
          label="Pipework warning notice issued?"
          name="gasPipeworkWarning"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasPipeworkWarningIssued,
            isComplete ? !cd.gasPipeworkWarningIssued : "",
            "gasPipeworkWarningYes",
            "gasPipeworkWarningNo",
          )}
        />
        <RadioQuestion
          label="Adey Prosense® installed and connected to customer's wifi?"
          name="adeyProsenseInstalled"
          disabled={isComplete}
          options={yesNoOptions(
            cd.gasAdeyProsenseOk,
            isComplete ? !cd.gasAdeyProsenseOk : "",
            "adeyProsenseInstalledYes",
            "adeyProsenseInstalledNo",
          )}
        />
        <div className="form-group mb-3" id="uploadCP1Question">
          {isComplete ? (
            <>
              <p className="text-muted">Uploaded CP1 Certificate</p>
              {cd.gasCP1Image?.url && <SignedImage src={cd.gasCP1Image.url} style={{ height: 300 }} />}
            </>
          ) : (
            <>
              <p className="mb-2">Upload CP1 or CP12 Gas Safety Certificate</p>
              <div className="d-flex flex-column flex-md-row gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => cameraInputRef.current.click()}
                >
                  Take Photo
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => uploadInputRef.current.click()}
                >
                  Upload File
                </button>
              </div>
              {selectedFileName && (
                <p className="text-muted mb-3">
                  <small>Selected: {selectedFileName}</small>
                </p>
              )}
              <input
                ref={cameraInputRef}
                type="file"
                name="gasCP1Image"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                className="d-none"
              />
              <input
                ref={uploadInputRef}
                type="file"
                name="gasCP1Image"
                accept="image/*, .pdf"
                onChange={handleFileChange}
                className="d-none"
              />
            </>
          )}
        </div>
        <div className="form-group mb-3" id="gasEngineerNotesQuestion">
          <label htmlFor="gasEngineerNotes">Engineer Notes</label>
          <textarea
            className="form-control"
            placeholder="enter any additional comments here"
            id="gasEngineerNotes"
            name="gasEngineerNotes"
            style={{ height: "150px" }}
            defaultValue={cd.gasEngineerNotes}
            disabled={isComplete}
          />
        </div>
        <div className="d-flex flex-column flex-md-row justify-content-md-end gap-2 mt-2" id="gasSignOffSubmit">
          <button onClick={gasSignOffHandler} type="submit" className="btn btn-primary" disabled={isComplete}>
            Submit Gas Sign Off
          </button>
        </div>
        <SectionErrors errorMessage={errorMessage} section="gasSignOff" />
      </form>
    </AccordionSection>
  );
}

GasSignOffSection.propTypes = {
  jobToComplete: PropTypes.object.isRequired,
  gasSignOffHandler: PropTypes.func.isRequired,
  uploadCP1ToClientFunc: PropTypes.func.isRequired,
  errorMessage: PropTypes.array.isRequired,
};
